import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, concatMap, finalize, map, switchMap, Observable, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '@core/services/user.service';
import { RsLoaderService } from '@lib/rs-ngx';
import { LockWorkOrdersService } from '@shared/services/leasing-company/work-orders/lock-work-orders.service';
import { LockWarningDialogComponent, LockWarningDialogData } from '@app/leasing-company/work-orders/shared/lock-warning-dialog/lock-warning-dialog.component';
import { WorkOrderLockInfo } from '@shared/models/work-orders/work-order-lock-info';
import { isNil } from 'lodash';

export function lockWorkOrderGuard(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
  const lockService = inject(LockWorkOrdersService);
  const dialog = inject(MatDialog);
  const router = inject(Router);
  const userId = inject(UserService).getUserId();
  const spinner = inject(RsLoaderService);
  const spinnerId = 'lock-work-order';
  const workOrderId = route.params['id'] as string;

  return fetchLockInfo().pipe(
    concatMap(({ canLock, lockingUserId, lockingUserName }) => {
      if (!canLock) {
        return of(true);
      }

      if (lockingUserId > 0 && lockingUserId !== userId) {
        return openLockDialog(lockingUserName).pipe(
          switchMap(confirmed => {
            if (confirmed) {
              return lockWorkOrder(true);
            }
            return handleReturningToWorkOrderOverview();
          }),
        );
      }

      return lockWorkOrder(false);
    }),
  );

  function fetchLockInfo(): Observable<WorkOrderLockInfo> {
    spinner.show({ id: spinnerId });
    return lockService.getWorkOrderLockInfo(workOrderId).pipe(
      finalize(() => spinner.hide({ id: spinnerId })),
    );
  }

  function openLockDialog(userName: string): Observable<boolean> {
    return dialog.open<LockWarningDialogComponent, LockWarningDialogData, boolean>(
      LockWarningDialogComponent, {
        width: '580px',
        autoFocus: false,
        restoreFocus: false,
        data: { userName },
      },
    )
      .afterClosed()
      .pipe(map(confirmed => confirmed === true));
  }

  function lockWorkOrder(force: boolean): Observable<boolean> {
    spinner.show({ id: spinnerId });
    return lockService.lockWorkOrder(workOrderId, force).pipe(
      finalize(() => spinner.hide({ id: spinnerId })),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  function handleReturningToWorkOrderOverview(): Observable<false | UrlTree> {
    const previousRoute = router.currentNavigation()?.previousNavigation?.finalUrl?.toString();
    if (isNil(previousRoute)) {
      return of(router.parseUrl('/leasing-company/work-orders'));
    }
    return of(false);
  }
}
